import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

import { parseFredCsv } from "@/lib/housing-momentum";
import {
  countyDisplayName,
  countyFips,
  countyPermitsSeriesId,
  hazardNarrative,
  loanLimitsForFips,
  summarizeCountyPermits,
  summarizeDisasters,
  type CountyPermits,
  type DisasterHistory,
  type DisasterRecord,
  type LoanLimits,
} from "@/lib/county-insights";

/**
 * County-level public data used on state and city pages:
 * FHFA conforming loan limits, Census building permits (via FRED) and FEMA
 * disaster declaration history. A feed that does not answer comes back as
 * `null`; nothing is estimated or substituted.
 */

export type CountyInsights = {
  stateAbbrev: string;
  countyName: string | null;
  countyFips: string | null;
  scope: "county" | "state";
  loanLimits: LoanLimits | null;
  permits: CountyPermits | null;
  disasters: DisasterHistory | null;
  hazardNarrative: string;
  fetchedAt: string;
};

const CACHE_MS = 1000 * 60 * 60 * 24;
const cache = new Map<string, { at: number; value: CountyInsights }>();

const DISASTER_SINCE = new Date().getUTCFullYear() - 10;

async function fetchPermits(fips: string) {
  try {
    const res = await fetch(
      `https://fred.stlouisfed.org/graph/fredgraph.csv?id=${countyPermitsSeriesId(fips)}`,
      { headers: { accept: "text/csv" }, signal: AbortSignal.timeout(8_000) },
    );
    if (!res.ok) return [];
    return parseFredCsv(await res.text());
  } catch (e) {
    console.error(`county permits ${fips} failed:`, (e as Error).message);
    return [];
  }
}

async function fetchDisasters(stateAbbrev: string, fips: string | null) {
  const filters = [
    `state eq '${stateAbbrev}'`,
    `fyDeclared ge ${DISASTER_SINCE}`,
    ...(fips
      ? [`fipsStateCode eq '${fips.slice(0, 2)}'`, `fipsCountyCode eq '${fips.slice(2)}'`]
      : []),
  ].join(" and ");
  const url =
    "https://www.fema.gov/api/open/v2/DisasterDeclarationsSummaries?" +
    new URLSearchParams({
      $filter: filters,
      $select: "incidentType,declarationTitle,declarationDate,fyDeclared",
      $top: "1000",
      $metadata: "off",
    }).toString();
  try {
    const res = await fetch(url, {
      headers: { accept: "application/json" },
      signal: AbortSignal.timeout(9_000),
    });
    if (!res.ok) return [];
    const json = (await res.json()) as {
      DisasterDeclarationsSummaries?: DisasterRecord[];
    };
    return json.DisasterDeclarationsSummaries ?? [];
  } catch (e) {
    console.error(`FEMA declarations ${stateAbbrev} failed:`, (e as Error).message);
    return [];
  }
}

const schema = z.object({
  stateAbbrev: z.string().length(2),
  countyName: z.string().min(2).max(60).optional(),
  placeLabel: z.string().min(2).max(80),
});

export const getCountyInsights = createServerFn({ method: "GET" })
  .inputValidator((d) => schema.parse(d))
  .handler(async ({ data }): Promise<CountyInsights> => {
    const state = data.stateAbbrev.toUpperCase();
    const fips = data.countyName ? countyFips(state, data.countyName) : null;
    const key = `${state}|${fips ?? "state"}`;
    const hit = cache.get(key);
    if (hit && Date.now() - hit.at < CACHE_MS) return hit.value;

    const [permitObs, disasterRows] = await Promise.all([
      fips ? fetchPermits(fips) : Promise.resolve([]),
      fetchDisasters(state, fips),
    ]);

    const disasters = summarizeDisasters(disasterRows, DISASTER_SINCE);
    const value: CountyInsights = {
      stateAbbrev: state,
      countyName: fips ? (countyDisplayName(fips) ?? data.countyName ?? null) : null,
      countyFips: fips,
      scope: fips ? "county" : "state",
      loanLimits: loanLimitsForFips(fips),
      permits: summarizeCountyPermits(permitObs),
      disasters,
      hazardNarrative: hazardNarrative(data.placeLabel, disasters),
      fetchedAt: new Date().toISOString(),
    };

    if (value.loanLimits || value.permits || value.disasters) {
      cache.set(key, { at: Date.now(), value });
    }
    return value;
  });
